export type LayoutFeature =
  | "fixtures"
  | "league-detail"
  | "league-standings"
  | "livescore"
  | "match-detail"
  | "odds"
  | "odds-board"
  | "results"
  | "team-detail";

export type LayoutRuntime = {
  feature: LayoutFeature;
  layout: string;
  rootClass: string;
  mount?: (root: HTMLElement, contract: any) => void;
};

const keyOf = (feature: string, layout: string): string => `${feature}:${layout}`;

export const createLayoutRegistry = (layouts: LayoutRuntime[]) => {
  const byKey = new Map(layouts.map((layout) => [keyOf(layout.feature, layout.layout), layout]));

  const resolve = (root: HTMLElement, contract: any): LayoutRuntime | null => {
    const feature = String(
      contract?.feature ||
        contract?.variant ||
        root.dataset.gwdFeature ||
        (root.classList.contains("gwd-fixtures-board") ? "fixtures" : "livescore"),
    );
    if (feature === "livescore" && root.dataset.gwdCustomLayout) {
      const custom = root.dataset.gwdCustomLayout;
      return { feature: "livescore", layout: custom, rootClass: `gwd-template-${custom}` };
    }
    const contractLayout = String(contract?.layout || "").trim();
    const classLayout = Array.from(root.classList)
      .find((className) => className.startsWith("gwd-livescore-layout--"))
      ?.replace("gwd-livescore-layout--", "");
    const layout = contractLayout || classLayout || "default";

    return (
      byKey.get(keyOf(feature, layout)) ||
      byKey.get(keyOf(feature, "default")) ||
      null
    );
  };

  const mount = (root: HTMLElement, contract: any): LayoutRuntime | null => {
    const layout = resolve(root, contract);
    if (!layout) return null;
    root.dataset.gwdResolvedFeature = layout.feature;
    root.dataset.gwdResolvedLayout = layout.layout;
    if (!root.classList.contains("gwd-livescore-layout"))
      root.classList.add("gwd-livescore-layout");
    Array.from(root.classList)
      .filter(
        (className) =>
          className.startsWith("gwd-livescore-layout--") &&
          className !== layout.rootClass,
      )
      .forEach((className) => root.classList.remove(className));
    if (layout.rootClass && !root.classList.contains(layout.rootClass))
      root.classList.add(layout.rootClass);
    layout.mount?.(root, contract);
    return layout;
  };

  return { mount, resolve };
};
